import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { OrganizationOverviewTabComponent } from './tabs/organization-overview-tab.component';
import { OrganizationMembersTabComponent } from './tabs/organization-members-tab.component';
import { CertificationsCardComponent } from '../../shared/components/certifications-card/certifications-card.component';
import { OrganizationDronesTabComponent } from './tabs/organization-drones-tab.component';
import { OrganizationSeasonsTabComponent } from './tabs/organization-seasons-tab.component';
import { OrganizationActivityTabComponent } from './tabs/organization-activity-tab.component';
import { OrganizationSprayRecordsTabComponent } from './tabs/organization-spray-records-tab.component';
import { OrganizationCustomersTabComponent } from './tabs/organization-customers-tab.component';
import { OrganizationTestResultsTabComponent } from './tabs/organization-test-results-tab.component';
import { OrganizationReportsTabComponent } from './tabs/organization-reports-tab.component';
import { OrganizationAnalysesTabComponent } from './tabs/organization-analyses-tab.component';
import { ConfirmationModalComponent } from '../../shared/components/confirmation-modal.component';
import { OrganizationMembershipService } from '../../core/data-services/organization-membership.service';
import { OrganizationService } from '../../core/data-services/organization.service';
import { Organization } from '../../core/data-models/organization.model';
import { OrganizationTabCounts } from '../../core/response-models/organization-tab-counts.model';
import { AdminMemberView } from '../../core/data-models/admin-member-view.model';
import { DroneService } from '../../core/data-services/drone.service';
import { CertificationService } from '../../core/data-services/certification.service';
import { ORGANIZATION_TYPE_LABELS } from 'src/app/shared/constants/organization-type-options';
import { OrganizationType } from 'src/app/core/data-models/organization-type.model';
import { getOrganizationTypeLabel } from 'src/app/core/utils/organization-type-utils';

interface OrganizationMember {
  id: string;
  profileId: string;
  role: string;
  firstName: string;
  lastName: string;
  email: string;
  joinedAt: string;
}

interface OrganizationStats {
  totalMembers: number;
  activeMembers: number;
  inactiveMembers: number;
  totalDrones: number;
  activeDrones: number;
  inactiveDrones: number;
  totalCertifications: number;
  validCertifications: number;
  expiringSoonCertifications: number;
  lastActivityAt: string | null;
}

type TabType = 'overview' | 'members' | 'drones' | 'certifications' | 'sprayRecords' | 'customers' | 'safetyTests' | 'seasons' | 'activity' | 'analyses' | 'reports';

@Component({
  selector: 'app-organization-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    OrganizationOverviewTabComponent,
    OrganizationMembersTabComponent,
    CertificationsCardComponent,
    OrganizationDronesTabComponent,
    OrganizationSeasonsTabComponent,
    OrganizationActivityTabComponent,
    OrganizationSprayRecordsTabComponent,
    OrganizationCustomersTabComponent,
    OrganizationTestResultsTabComponent,
    OrganizationReportsTabComponent,
    OrganizationAnalysesTabComponent,
    ConfirmationModalComponent
  ],
  templateUrl: './organization-detail.component.html',
  styleUrl: './organization-detail.component.scss'
})
export class OrganizationDetailComponent implements OnInit {
  organizationId!: string;
  organization: Organization | null = null;
  members: AdminMemberView[] = [];
  stats: OrganizationStats = {
    totalMembers: 0,
    activeMembers: 0,
    inactiveMembers: 0,
    totalDrones: 0,
    activeDrones: 0,
    inactiveDrones: 0,
    totalCertifications: 0,
    validCertifications: 0,
    expiringSoonCertifications: 0,
    lastActivityAt: null
  };
  ownerProfile: OrganizationMember | null = null;
  chiefSupervisorProfile: OrganizationMember | null = null;

  loading = true;
  loadingMembers = false;
  errorMessage = '';
  successMessage = '';

  // Tab state
  activeTab: TabType = 'overview';
  tabCounts: OrganizationTabCounts | null = null;
  loadedTabs: Set<TabType> = new Set<TabType>();

  // Confirmation modal state
  showConfirmModal = false;
  confirmModalAction: 'archive' | 'unarchive' | 'delete' | null = null;
  confirmModalProcessing = false;

  // Organization types for display
  organizationTypes = ORGANIZATION_TYPE_LABELS;

  private readonly organizationService = inject(OrganizationService);
  private readonly membershipService = inject(OrganizationMembershipService);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private droneService: DroneService,
    private certificationService: CertificationService
  ) {}

  ngOnInit(): void {
    this.organizationId = this.route.snapshot.params['id'];

    // Handle tab query parameter
    this.route.queryParams.subscribe(params => {
      const tab = params['tab'] as TabType;
      if (tab && this.isValidTab(tab)) {
        this.activeTab = tab;
      }
    });

    this.loadOrganization();
  }

  isValidTab(tab: string): tab is TabType {
    return ['overview', 'members', 'drones', 'certifications', 'sprayRecords', 'customers', 'safetyTests', 'seasons', 'activity', 'analyses', 'reports'].includes(tab);
  }

  async loadOrganization(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      this.organization = await firstValueFrom(this.organizationService.getOrganization(this.organizationId));
      if (this.organization) {
        await this.loadTabCounts();
        this.onTabActivated(this.activeTab);
      }
    } catch (error: any) {
      console.error('Failed to load organization:', error);
      this.errorMessage = 'Failed to load organization details';
    } finally {
      this.loading = false;
    }
  }

  /**
   * Load tab counts from the API for badge display
   */
  async loadTabCounts(): Promise<void> {
    try {
      this.tabCounts = await firstValueFrom(this.organizationService.getTabCounts(this.organizationId));
    } catch (error: any) {
      console.error('Failed to load tab counts:', error);
    }
  }

  /**
   * Lazy-load data for a tab on first activation.
   * Subsequent activations skip re-fetching.
   */
  onTabActivated(tab: TabType): void {
    if (this.loadedTabs.has(tab)) return;

    this.loadedTabs.add(tab);

    switch (tab) {
      case 'overview':
        this.loadMembers().then(() => this.calculateStats());
        break;
      case 'members':
        if (!this.loadedTabs.has('overview')) {
          this.loadMembers();
        }
        break;
      case 'drones':
        this.loadDroneStats();
        break;
      case 'certifications':
        this.loadCertificationStats();
        break;
      case 'sprayRecords':
      case 'customers':
      case 'safetyTests':
        // These tabs handle their own data loading via @Input() organizationId
        break;
      case 'seasons':
      case 'activity':
      case 'analyses':
      case 'reports':
        // These tabs load their own data internally
        break;
    }
  }

  async loadMembers(): Promise<void> {
    this.loadingMembers = true;

    try {
      const result = await this.membershipService.getOrganizationMembers(
        this.organizationId,
        'all' // Load both active and inactive
      );

      this.members = result.members;

      // Update stats from API
      this.stats.totalMembers = result.stats.total;
      this.stats.activeMembers = result.stats.active;
      this.stats.inactiveMembers = result.stats.inactive;

      // Find owner and chief supervisor profiles and convert to OrganizationMember for overview tab
      if (this.organization?.ownerProfileId) {
        const ownerMember = this.members.find(
          m => m.profileId === this.organization!.ownerProfileId
        );
        if (ownerMember) {
          this.ownerProfile = this.convertToOrgMember(ownerMember);
        }
      }
      if (this.organization?.chiefSupervisorProfileId) {
        const csMember = this.members.find(
          m => m.profileId === this.organization!.chiefSupervisorProfileId
        );
        if (csMember) {
          this.chiefSupervisorProfile = this.convertToOrgMember(csMember);
        }
      }
    } catch (error: any) {
      console.error('Failed to load members:', error);
    } finally {
      this.loadingMembers = false;
    }
  }

  calculateStats(): void {
    // Members stats are loaded from API in loadMembers()

    // Initialize placeholder values
    this.stats.totalDrones = 0;
    this.stats.activeDrones = 0;
    this.stats.inactiveDrones = 0;
    this.stats.totalCertifications = 0;
    this.stats.validCertifications = 0;
    this.stats.expiringSoonCertifications = 0;
    this.stats.lastActivityAt = null;

    // Load drone counts
    this.loadDroneStats();

    // Load certification counts
    this.loadCertificationStats();
  }

  /**
   * Load drone stats for the organization
   */
  private loadDroneStats(): void {
    this.droneService.getOrganizationDrones(this.organizationId).subscribe({
      next: (response) => {
        const adnCount = response.adnAssigned?.length || 0;
        const orgCount = response.organizationOwned?.length || 0;
        this.stats.totalDrones = adnCount + orgCount;

        // Count active drones
        const activeAdn = response.adnAssigned?.filter(d => d.status === 'assigned' || d.status === 'active').length || 0;
        const activeOrg = response.organizationOwned?.filter(d => d.status === 'active').length || 0;
        this.stats.activeDrones = activeAdn + activeOrg;
        this.stats.inactiveDrones = this.stats.totalDrones - this.stats.activeDrones;
      },
      error: (err) => {
        console.error('Failed to load drone stats:', err);
      }
    });
  }

  /**
   * Load certification stats for the organization
   */
  private loadCertificationStats(): void {
    this.certificationService.getOrganizationCertifications(this.organizationId).subscribe({
      next: (certifications) => {
        this.stats.totalCertifications = certifications.length;

        // Count valid and expiring soon
        const now = new Date();
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

        this.stats.validCertifications = certifications.filter(c =>
          c.status === 'active'
        ).length;

        this.stats.expiringSoonCertifications = certifications.filter(c => {
          if (!c.expirationDate) return false;
          const expDate = new Date(c.expirationDate);
          return expDate > now && expDate <= thirtyDaysFromNow;
        }).length;
      },
      error: (err) => {
        console.error('Failed to load certification stats:', err);
      }
    });
  }

  /**
   * Handle drones changed event from drones tab
   */
  onDronesChanged(): void {
    this.loadDroneStats();
  }

  /**
   * Handle members updated event from members tab
   */
  async onMembersUpdated(): Promise<void> {
    // Reset loaded tabs to allow re-fetching
    this.loadedTabs.clear();
    await this.loadOrganization();
  }

  setActiveTab(tab: TabType): void {
    this.activeTab = tab;
    // Update URL with tab parameter
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge'
    });
    this.onTabActivated(tab);
  }

    getOrganizationTypeLabel = getOrganizationTypeLabel
  getOrganizationTypeClass(type: OrganizationType | null | undefined): string {
    switch (type) {
      case OrganizationType.Llc:
      case OrganizationType.SCorp:
      case OrganizationType.CCorp:
        return 'bg-primary';
      case OrganizationType.Partnership:
        return 'bg-info';
      case OrganizationType.SoleProprietorship:
      case OrganizationType.Individual:
        return 'bg-secondary';
      case OrganizationType.NonProfit:
        return 'bg-success';
      default:
        return 'bg-secondary';
    }
  }

  formatAddress(): string {
    if (!this.organization) return '';

    const parts: string[] = [];
    parts.push(this.organization.streetAddressLine1 ?? '');
    if (this.organization.streetAddressLine2) {
      parts.push(this.organization.streetAddressLine2);
    }
    parts.push(`${this.organization.city}, ${this.organization.state} ${this.organization.zipCode}`);
    parts.push(this.organization.country ?? '');

    return parts.join('\n');
  }

  formatDate(dateString: Date | string | null | undefined): string {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatShortDate(dateString: Date | string | null | undefined): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  goBack(): void {
    this.router.navigate(['/organizations']);
  }

  editOrganization(): void {
    this.router.navigate(['/organizations', this.organizationId, 'edit']);
  }

  archiveOrganization(): void {
    this.confirmModalAction = 'archive';
    this.showConfirmModal = true;
  }

  unarchiveOrganization(): void {
    this.confirmModalAction = 'unarchive';
    this.showConfirmModal = true;
  }

  async onConfirmAction(): Promise<void> {
    if (!this.organization || !this.confirmModalAction) return;

    this.confirmModalProcessing = true;

    try {
      if (this.confirmModalAction === 'archive') {
        await firstValueFrom(this.organizationService.archive(this.organizationId));
        this.showConfirmModal = false;
        this.confirmModalProcessing = false;
        this.router.navigate(['/organizations'], {
          state: { successMessage: `"${this.organization!.name}" has been archived successfully.` }
        });
      } else if (this.confirmModalAction === 'unarchive') {
        await firstValueFrom(this.organizationService.unarchive(this.organizationId));
        this.showConfirmModal = false;
        this.confirmModalProcessing = false;
        this.successMessage = `"${this.organization!.name}" has been restored successfully.`;
        this.loadedTabs.clear();
        await this.loadOrganization();
      } else if (this.confirmModalAction === 'delete') {
        await firstValueFrom(this.organizationService.softDelete(this.organizationId));
        this.showConfirmModal = false;
        this.confirmModalProcessing = false;
        this.router.navigate(['/organizations'], {
          state: { successMessage: `"${this.organization!.name}" has been deleted successfully.` }
        });
      }
    } catch (error: any) {
      console.error(`${this.confirmModalAction} organization error:`, error);
      this.errorMessage = `Failed to ${this.confirmModalAction} organization`;
      this.confirmModalProcessing = false;
      this.showConfirmModal = false;
    }
  }

  async onMembershipChanged(message: string): Promise<void> {
    this.successMessage = message;
    await this.loadOrganization();
  }

  onCancelConfirm(): void {
    this.showConfirmModal = false;
    this.confirmModalAction = null;
    this.confirmModalProcessing = false;
  }

  deleteOrganization(): void {
    this.confirmModalAction = 'delete';
    this.showConfirmModal = true;
  }

  /**
   * Convert AdminMemberView to OrganizationMember for overview tab
   */
  private convertToOrgMember(member: AdminMemberView): OrganizationMember {
    return {
      id: member.membershipId,
      profileId: member.profileId,
      role: String(member.role),
      firstName: member.firstName || '',
      lastName: member.lastName || '',
      email: member.email ?? '',
      joinedAt: member.createdAt?.toISOString() ?? ''
    };
  }
}
